import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Save,
  Sparkles,
  CheckCircle,
  AlertCircle,
  XCircle,
  Copy,
  Trash2
} from 'lucide-react';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ALL_DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DEFAULT_SPECIAL_DAYS = ['Sunday'];

const DraggableRecipe = ({ recipe, category, isUsed }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `recipe-${recipe.id}`,
    data: { recipe, category }
  });

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={`recipe-drag-item category-sunday ${isDragging ? 'opacity-50' : ''} ${isUsed ? 'bg-yellow-100 border-yellow-400' : ''}`}
      title={isUsed ? 'Used in rotation' : 'Not used yet'}
    >
      <span className="flex items-center gap-2">
        {isUsed && <span className="w-2 h-2 rounded-full bg-yellow-500 flex-shrink-0"></span>}
        {recipe.recipe_name}
      </span>
    </div>
  );
};

const DroppableSlot = ({ week, day, slot, value, onClear }) => {
  const { isOver, setNodeRef } = useDroppable({
    id: `${week}-${day}-${slot}`,
    data: { week, day, slot }
  });

  const slotLabels = {
    main: 'Main Item',
    roti: 'Roti',
    raita: 'Raita',
    side: 'Side'
  };

  return (
    <div
      ref={setNodeRef}
      className={`slot-container ${value ? 'filled' : ''} ${isOver ? 'drop-active' : ''}`}
    >
      {value ? (
        <div className="w-full flex items-center justify-between gap-1">
          <span className="text-xs font-semibold text-[#d6922b] truncate flex-1">
            {value}
          </span>
          <button
            onClick={() => onClear(week, day, slot)}
            className="p-1 hover:bg-red-100 text-red-500 rounded flex-shrink-0"
            title="Clear"
          >
            <XCircle size={12} />
          </button>
        </div>
      ) : (
        <span className="text-xs text-stone-400">{slotLabels[slot]}</span>
      )}
    </div>
  );
};

export const SpecialRotation = () => {
  const [recipes, setRecipes] = useState([]);
  const [rotations, setRotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [usedRecipes, setUsedRecipes] = useState(new Set());

  const [rotationLength, setRotationLength] = useState(1);
  const [selectedDays, setSelectedDays] = useState(DEFAULT_SPECIAL_DAYS);
  const [schedule, setSchedule] = useState({});
  const [rotationId, setRotationId] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [recipesRes, specialRotRes, regularRotRes] = await Promise.all([
        axios.get(`${API}/recipes`),
        axios.get(`${API}/rotations/special`),
        axios.get(`${API}/rotations/regular`)
      ]);
      setLoadFailed(false);
      
      const eligibleRecipes = recipesRes.data.filter(r => 
        r.visibility_tag === 'Special Rotation Eligible' && r.active
      );
      setRecipes(eligibleRecipes);

      // Collect all used recipe names from both rotations
      const used = new Set();
      
      // Check regular rotation
      if (regularRotRes.data.length > 0) {
        const regularRot = regularRotRes.data[0];
        const regularSchedule = regularRot.schedule || {};
        Object.values(regularSchedule).forEach(week => {
          Object.values(week).forEach(day => {
            if (day.sabzi) used.add(day.sabzi);
            if (day.daal) used.add(day.daal);
            if (day.salad) used.add(day.salad);
          });
        });
      }

      if (specialRotRes.data.length > 0) {
        const rotation = specialRotRes.data[0];
        setRotationId(rotation.id);
        setRotationLength(rotation.rotation_length);
        setSelectedDays(rotation.days || DEFAULT_SPECIAL_DAYS);
        
        // Handle backward compatibility for old schedule format
        let scheduleData = rotation.schedule || {};
        const firstWeek = scheduleData['Week1'];
        if (firstWeek && !firstWeek[Object.keys(firstWeek)[0]]?.hasOwnProperty) {
          if (firstWeek.main !== undefined || firstWeek.roti !== undefined || firstWeek.raita !== undefined || firstWeek.side !== undefined) {
            const days = rotation.days || DEFAULT_SPECIAL_DAYS;
            const convertedSchedule = {};
            for (let w = 1; w <= rotation.rotation_length; w++) {
              const weekKey = `Week${w}`;
              const oldWeek = scheduleData[weekKey];
              if (oldWeek) {
                convertedSchedule[weekKey] = {};
                days.forEach(day => {
                  convertedSchedule[weekKey][day] = {
                    main: oldWeek.main || null,
                    roti: oldWeek.roti || null,
                    raita: oldWeek.raita || null,
                    side: oldWeek.side || null
                  };
                });
              }
            }
            scheduleData = convertedSchedule;
          }
        }
        setSchedule(scheduleData);
        
        // Collect used recipes from Special rotation
        Object.values(scheduleData).forEach(week => {
          Object.values(week).forEach(day => {
            if (day.main) used.add(day.main);
            if (day.roti) used.add(day.roti);
            if (day.raita) used.add(day.raita);
            if (day.side) used.add(day.side);
          });
        });
      } else {
        initializeSchedule(1, DEFAULT_SPECIAL_DAYS);
      }
      
      setUsedRecipes(used);
    } catch (error) {
      setLoadFailed(true);
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const initializeSchedule = (length, days) => {
    const newSchedule = {};
    for (let w = 1; w <= length; w++) {
      newSchedule[`Week${w}`] = {};
      days.forEach(day => {
        newSchedule[`Week${w}`][day] = {
          main: null,
          roti: null,
          raita: null,
          side: null
        };
      });
    }
    setSchedule(newSchedule);
  };

  const handleRotationLengthChange = (length) => {
    const numLength = parseInt(length);
    const newSchedule = { ...schedule };
    for (let w = 1; w <= numLength; w++) {
      const weekKey = `Week${w}`;
      if (!newSchedule[weekKey]) {
        newSchedule[weekKey] = {};
        selectedDays.forEach(day => {
          newSchedule[weekKey][day] = {
            main: null,
            roti: null,
            raita: null,
            side: null
          };
        });
      }
    }
    Object.keys(newSchedule).forEach(key => {
      const weekNum = parseInt(key.replace('Week', ''));
      if (weekNum > numLength) {
        delete newSchedule[key];
      }
    });
    setRotationLength(numLength);
    setSchedule(newSchedule);
  };

  const handleDayToggle = (day) => {
    let newDays;
    if (selectedDays.includes(day)) {
      if (selectedDays.length === 1) {
        toast.error('You must have at least one day selected');
        return;
      }
      newDays = selectedDays.filter(d => d !== day);
    } else {
      newDays = ALL_DAYS.filter(d => selectedDays.includes(d) || d === day);
    }
    
    setSelectedDays(newDays);
    
    const newSchedule = { ...schedule };
    for (let w = 1; w <= rotationLength; w++) {
      const weekKey = `Week${w}`;
      if (!newSchedule[weekKey]) {
        newSchedule[weekKey] = {};
      }
      if (!selectedDays.includes(day) && !newSchedule[weekKey][day]) {
        newSchedule[weekKey][day] = {
          main: null,
          roti: null,
          raita: null,
          side: null
        };
      }
      if (selectedDays.includes(day) && !newDays.includes(day)) {
        delete newSchedule[weekKey][day];
      }
    }
    setSchedule(newSchedule);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (!over) return;

    const { recipe, category } = active.data.current;
    const idParts = over.id.split('-');
    const week = idParts[0];
    const day = idParts[1];
    const slot = idParts[2];

    const slotCategoryMap = {
      main: 'SpecialMain',
      roti: 'SpecialRoti',
      raita: 'SpecialRaita',
      side: 'SpecialSide'
    };

    if (slotCategoryMap[slot] !== category) {
      toast.error(`Cannot drop ${category} recipe in ${slot} slot`);
      return;
    }

    setSchedule(prev => ({
      ...prev,
      [week]: {
        ...prev[week],
        [day]: {
          ...(prev[week]?.[day] || {}),
          [slot]: recipe.recipe_name
        }
      }
    }));
  };

  const clearSlot = (week, day, slot) => {
    setSchedule(prev => ({
      ...prev,
      [week]: {
        ...prev[week],
        [day]: {
          ...(prev[week]?.[day] || {}),
          [slot]: null
        }
      }
    }));
  };

  const handleSave = async () => {
    if (loadFailed) {
      toast.error('Could not load your existing rotation. Please reload the page before saving to avoid losing data.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: 'Special Rotation',
        rotation_length: rotationLength,
        days: selectedDays,
        schedule
      };

      if (rotationId) {
        await axios.put(`${API}/rotations/special/${rotationId}`, payload);
      } else {
        const response = await axios.post(`${API}/rotations/special`, payload);
        setRotationId(response.data.id);
      }
      toast.success('Special rotation saved successfully');
    } catch (error) {
      toast.error('Failed to save rotation');
    } finally {
      setSaving(false);
    }
  };

  const getDayStatus = (weekSchedule, day) => {
    if (!weekSchedule || !weekSchedule[day]) return 'incomplete';
    const slots = weekSchedule[day];
    const filled = [slots.main, slots.roti, slots.raita, slots.side].filter(Boolean).length;
    if (filled === 4) return 'complete';
    if (filled > 0) return 'partial';
    return 'incomplete';
  };

  const getWeekCompletion = (weekKey) => {
    if (!schedule[weekKey]) return { complete: 0, total: selectedDays.length };
    let complete = 0;
    selectedDays.forEach(day => {
      if (getDayStatus(schedule[weekKey], day) === 'complete') complete++;
    });
    return { complete, total: selectedDays.length };
  };

  const mainRecipes = recipes.filter(r => r.category === 'SpecialMain');
  const rotiRecipes = recipes.filter(r => r.category === 'SpecialRoti');
  const raitaRecipes = recipes.filter(r => r.category === 'SpecialRaita');
  const sideRecipes = recipes.filter(r => r.category === 'SpecialSide');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <DndContext
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <div className="space-y-6" data-testid="special-rotation-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-stone-800 tracking-tight flex items-center gap-3" >
              <Sparkles className="text-[#faa023]" size={36} />
              Special Rotation
            </h1>
            <p className="text-stone-500 mt-1">Special menu planning for selected days</p>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn-secondary text-black"
            data-testid="save-special-rotation-btn"
          >
            {saving ? <span className="spinner" /> : <Save size={20} />}
            <span>Save Rotation</span>
          </button>
        </div>

        {/* Settings */}
        <div className="virsa-card p-4 border-2 border-[#faa023]">
          <div className="space-y-4">
            <div className="flex flex-col md:flex-row gap-4 items-start">
              <div className="flex-1">
                <label className="block text-sm font-medium text-stone-600 mb-2">Rotation Length</label>
                <Select value={rotationLength.toString()} onValueChange={handleRotationLengthChange}>
                  <SelectTrigger className="h-12" data-testid="special-rotation-length-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 Week</SelectItem>
                    <SelectItem value="2">2 Weeks</SelectItem>
                    <SelectItem value="3">3 Weeks</SelectItem>
                    <SelectItem value="4">4 Weeks</SelectItem>
                    <SelectItem value="5">5 Weeks</SelectItem>
                    <SelectItem value="6">6 Weeks</SelectItem>
                    <SelectItem value="7">7 Weeks</SelectItem>
                    <SelectItem value="8">8 Weeks</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="text-center md:text-right flex-1">
                <p className="text-sm text-stone-500">
                  Special menus rotate independently from weekday menus
                </p>
              </div>
            </div>
            
            {/* Day Selection */}
            <div>
              <label className="block text-sm font-medium text-stone-600 mb-3">Active Days</label>
              <div className="flex flex-wrap gap-3">
                {ALL_DAYS.map(day => {
                  const isSelected = selectedDays.includes(day);
                  return (
                    <button
                      type="button"
                      key={day}
                      onClick={() => handleDayToggle(day)}
                      style={isSelected ? { backgroundColor: '#faa023', borderColor: '#faa023', color: 'white' } : {}}
                      className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border-2 cursor-pointer transition-all font-semibold ${
                        isSelected 
                          ? '' 
                          : 'border-stone-200 bg-white text-stone-500 hover:border-stone-300'
                      }`}
                      data-testid={`special-day-toggle-${day.toLowerCase()}`}
                    >
                      <div 
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                          isSelected 
                            ? 'border-white bg-white' 
                            : 'border-stone-300 bg-white'
                        }`}
                      >
                        {isSelected && (
                          <svg className="w-3 h-3" style={{ color: '#faa023' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                      <span className="text-sm font-medium">{day.slice(0, 3)}</span>
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-stone-400 mt-2">
                {selectedDays.length} day(s) selected for special menu
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recipe Lists */}
          <div className="space-y-4">
            {/* Legend for yellow indicator */}
            <div className="text-xs text-stone-500 flex items-center gap-2 px-2">
              <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
              <span>Used in rotation</span>
            </div>
            
            <div className="virsa-card p-4 border-2 border-[#faa023]/30">
              <h3 className="font-bold text-stone-800 mb-3 flex items-center gap-2">
                <span className="sunday-badge">Main</span>
                ({mainRecipes.length})
              </h3>
              <div className="max-h-40 overflow-y-auto space-y-1">
                {mainRecipes.map(recipe => (
                  <DraggableRecipe 
                    key={recipe.id} 
                    recipe={recipe} 
                    category="SpecialMain" 
                    isUsed={usedRecipes.has(recipe.recipe_name)}
                  />
                ))}
                {mainRecipes.length === 0 && (
                  <p className="text-sm text-stone-400 italic">No Special Main recipes</p>
                )}
              </div>
            </div>

            <div className="virsa-card p-4 border-2 border-[#faa023]/30">
              <h3 className="font-bold text-stone-800 mb-3 flex items-center gap-2">
                <span className="sunday-badge">Roti</span>
                ({rotiRecipes.length})
              </h3>
              <div className="max-h-40 overflow-y-auto space-y-1">
                {rotiRecipes.map(recipe => (
                  <DraggableRecipe 
                    key={recipe.id} 
                    recipe={recipe} 
                    category="SpecialRoti" 
                    isUsed={usedRecipes.has(recipe.recipe_name)}
                  />
                ))}
                {rotiRecipes.length === 0 && (
                  <p className="text-sm text-stone-400 italic">No Special Roti recipes</p>
                )}
              </div>
            </div>

            <div className="virsa-card p-4 border-2 border-[#faa023]/30">
              <h3 className="font-bold text-stone-800 mb-3 flex items-center gap-2">
                <span className="sunday-badge">Raita</span>
                ({raitaRecipes.length})
              </h3>
              <div className="max-h-40 overflow-y-auto space-y-1">
                {raitaRecipes.map(recipe => (
                  <DraggableRecipe 
                    key={recipe.id} 
                    recipe={recipe} 
                    category="SpecialRaita" 
                    isUsed={usedRecipes.has(recipe.recipe_name)}
                  />
                ))}
                {raitaRecipes.length === 0 && (
                  <p className="text-sm text-stone-400 italic">No Special Raita recipes</p>
                )}
              </div>
            </div>

            <div className="virsa-card p-4 border-2 border-[#faa023]/30">
              <h3 className="font-bold text-stone-800 mb-3 flex items-center gap-2">
                <span className="sunday-badge">Side</span>
                ({sideRecipes.length})
              </h3>
              <div className="max-h-40 overflow-y-auto space-y-1">
                {sideRecipes.map(recipe => (
                  <DraggableRecipe 
                    key={recipe.id} 
                    recipe={recipe} 
                    category="SpecialSide" 
                    isUsed={usedRecipes.has(recipe.recipe_name)}
                  />
                ))}
                {sideRecipes.length === 0 && (
                  <p className="text-sm text-stone-400 italic">No Special Side recipes</p>
                )}
              </div>
            </div>
          </div>

          {/* Schedule Grid */}
          <div className="lg:col-span-2 space-y-6">
            {Array.from({ length: rotationLength }, (_, i) => i + 1).map(weekNum => {
              const weekKey = `Week${weekNum}`;
              const completion = getWeekCompletion(weekKey);

              return (
                <div key={weekKey} className="virsa-card p-4 border-2 border-[#faa023]/50">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-stone-800">
                      Week {weekNum}
                    </h3>
                    <span className={`badge ${completion.complete === completion.total ? 'badge-success' : 'badge-warning'}`}>
                      {completion.complete}/{completion.total} days
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {selectedDays.map(day => {
                      const status = getDayStatus(schedule[weekKey], day);
                      const statusIcon = {
                        complete: <CheckCircle size={16} className="text-green-600" />,
                        partial: <AlertCircle size={16} className="text-orange-500" />,
                        incomplete: <XCircle size={16} className="text-red-500" />
                      };

                      return (
                        <div
                          key={day}
                          className={`virsa-card overflow-hidden border-2 ${status === 'complete' ? 'border-green-400' : status === 'partial' ? 'border-orange-400' : 'border-[#faa023]'}`}
                        >
                          <div className="bg-[#faa023] text-black p-2 flex items-center justify-between">
                            <span className="font-bold text-sm">
                              {day}
                            </span>
                            {statusIcon[status]}
                          </div>
                          <div className="p-3 space-y-2">
                            <div>
                              <label className="text-xs font-semibold text-stone-500 uppercase mb-1 block">Main Item</label>
                              <DroppableSlot
                                week={weekKey}
                                day={day}
                                slot="main"
                                value={schedule[weekKey]?.[day]?.main}
                                onClear={clearSlot}
                              />
                            </div>
                            <div>
                              <label className="text-xs font-semibold text-stone-500 uppercase mb-1 block">Roti</label>
                              <DroppableSlot
                                week={weekKey}
                                day={day}
                                slot="roti"
                                value={schedule[weekKey]?.[day]?.roti}
                                onClear={clearSlot}
                              />
                            </div>
                            <div>
                              <label className="text-xs font-semibold text-stone-500 uppercase mb-1 block">Raita</label>
                              <DroppableSlot
                                week={weekKey}
                                day={day}
                                slot="raita"
                                value={schedule[weekKey]?.[day]?.raita}
                                onClear={clearSlot}
                              />
                            </div>
                            <div>
                              <label className="text-xs font-semibold text-stone-500 uppercase mb-1 block">Side</label>
                              <DroppableSlot
                                week={weekKey}
                                day={day}
                                slot="side"
                                value={schedule[weekKey]?.[day]?.side}
                                onClear={clearSlot}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </DndContext>
  );
};
